import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useQCM } from '../../contexts/QCMContext';
import QCMContent from './QCMContent';
import QCMOptions from './QCMOptions';
import ExamQCMOptions from './ExamQCMOptions';
import QCMSolution from './QCMSolution';
import QCMHelp from './QCMHelp';
import QCMTextInput from './QCMTextInput';
import QCMSummary from './QCMSummary';
import QCMKnowledgeReinforcement from './QCMKnowledgeReinforcement';
import QCMConceptualMapping from './QCMConceptualMapping';
import BeTheTeacher from './BeTheTeacher';
import OpenQuestion from './OpenQuestion';
import QuizModeSelector from './QuizModeSelector';
import FunnelQCMViewer from './FunnelQCMViewer';
import QCMRating from './QCMRating';

import FeedbackDisplay from '../interactive/FeedbackDisplay';
import Timer from '../interactive/Timer';
import NotesEditor from '../interactive/NotesEditor';
import InfoPanel from '../common/InfoPanel';
import ExamInfoPanel from './ExamInfoPanel';
import UserMetricsPanel from '../common/UserMetricsPanel';
import SectionContent from '../lab/SectionContent';
import ContextDisplay from '../dashboard/ContextDisplay';
import QCMDashboard from './QCMDashboard';

import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import { useTranslation } from 'react-i18next';
import SearchBox from '../search/SearchBox';
import {
    getMergedRevisionStrategyFromStorage,
    saveRevisionStrategyToStorage,
} from '../../utils/qcmInitialLoad';

const QCMViewer = () => {
    const { t } = useTranslation();
    const {
        qcmData,
        currentQCM,
        currentQCMIndex,
        currentQuestionType,
        loading,
        evaluationLoading,
        error,
        feedback,
        displayMode,
        showSummary,
        showKnowledgeReinforcement,
        options,
        attemptCount,
        elapsedTime,
        maxTime,
        isTimerRunning,
        degreeOfCertainty,
        hoveredCertainty,
        sessionContext,
        setDisplayMode,
        setIsTimerRunning,
        getNextQCM,
        getSimilarQCM,
        goToNextFunnelStep
    } = useQCM();

    const [renderPanels, setRenderPanels] = useState(false);
    
    // Fonction pour gérer la sélection d'un QCM depuis la recherche
    const handleSearchItemSelect = useCallback(async (item, questionType) => {
        if (questionType === 'qcm') {
            // Charger le QCM sélectionné
            try {
                // Ici vous pouvez utiliser votre logique existante pour charger un QCM spécifique
                // Par exemple, appeler une fonction du contexte QCM
                console.log('QCM sélectionné:', item);
                // Exemple d'implémentation - à adapter selon votre contexte
                // await loadSpecificQCM(item._id);
            } catch (error) {
                console.error('Erreur lors du chargement du QCM:', error);
            }
        }
    }, []);

    useEffect(() => {
        if (!loading && qcmData && currentQCM) {
            const timer = setTimeout(() => {
                setRenderPanels(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            setRenderPanels(false);
        }
    }, [loading, qcmData, currentQCM]);

    React.useEffect(() => {
        const onNextQcm = () => {
            getNextQCM();
        };
        const onNextStep = () => {
            goToNextFunnelStep();
        };
        const persistRevisionStrategy = (ev) => {
            const detail = ev.detail || {};
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            const uid = String(u.username || u._id || u.id || sessionContext?.user || 'anonymous_user');
            try {
                const prev = getMergedRevisionStrategyFromStorage(
                    uid,
                    sessionContext?.exam,
                    sessionContext?.sessionId || '111222'
                );
                const next = { ...prev };
                if (detail.filterType) next.filterType = detail.filterType;
                if (detail.studyMode) next.studyMode = detail.studyMode;
                saveRevisionStrategyToStorage(
                    uid,
                    sessionContext?.exam,
                    sessionContext?.sessionId || '111222',
                    next
                );
            } catch (_) {
                /* ignore */
            }
        };
        window.addEventListener('requestNextQCM', onNextQcm);
        window.addEventListener('requestNextFunnelStep', onNextStep);
        window.addEventListener('apprentissage:set-revision-strategy', persistRevisionStrategy);
        return () => {
            window.removeEventListener('requestNextQCM', onNextQcm);
            window.removeEventListener('requestNextFunnelStep', onNextStep);
            window.removeEventListener('apprentissage:set-revision-strategy', persistRevisionStrategy);
        };
    }, [getNextQCM, goToNextFunnelStep, sessionContext?.user, sessionContext?.exam, sessionContext?.sessionId]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900 text-blue-400 text-xl">
                {t('loading_qcm')}
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900 text-red-500 text-xl">
                {t('error')}: {error}
            </div>
        );
    }

    if (!qcmData || !currentQCM) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900 text-yellow-400 text-xl">
                {t('no_qcm_data')}
            </div>
        );
    }

    const renderMainContent = () => {
        switch (displayMode) {
            case 'qcm':
                return <QCMOptions />;
            case 'solution':
                return <QCMSolution />;
            case 'help':
                return <QCMHelp />;
            default:
                return <QCMContent />;
        }
    };

    const renderRightPanel = () => {
        // Normal behavior - show text input for question mode
        if (displayMode === 'question') {
            return <QCMTextInput />;
        }
        
        // For other modes, show summary and conceptual mapping
        return (
            <div className="flex flex-col h-full">


                {/* Summary Section - Right side */}
                {showSummary && (
                    <div className="flex-shrink-0 mb-4">
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-blue-400 mb-4 flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                {t('summary')}
                            </h3>
                            
                            {/* Distractor Analysis - Now first */}
                            {currentQCM?.summary?.distractor_failures && (
                                <div className="mb-4">
                                    <h4 className="text-md font-semibold text-red-300 mb-2">
                                        Why Other Options Are Incorrect:
                                    </h4>
                                    <div className="space-y-2">
                                        {Object.entries(currentQCM.summary.distractor_failures).map(([approach, failure], index) => (
                                            <div key={index} className="bg-gray-800 rounded p-3">
                                                <div className="flex-grow">
                                                    <p className="font-medium text-red-300 mb-1">{approach}:</p>
                                                    <p className="text-gray-400 text-sm">{failure}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Problem-Solution Summary - Now after distractor analysis */}
                            {currentQCM?.summary?.problem_solution && (
                                <div>
                                    <h4 className="text-md font-semibold text-blue-300 mb-2">
                                        Summary:
                                    </h4>
                                    <div className="bg-gray-800 rounded p-3">
                                        <p className="text-gray-300 leading-relaxed">
                                            {currentQCM.summary.problem_solution}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Tips removed from here - moved to QCMSolution */}
                        </div>
                    </div>
                )}

                {/* Map the solution Section - Only show when solution is displayed */}
                {displayMode === 'solution' && currentQCM?.conceptual_Mapping && (
                    <div className="flex-shrink-0 mb-4">
                        <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-indigo-500 shadow-lg">
                            <QCMConceptualMapping />
                        </div>
                    </div>
                )}

                {/* Be the Teacher Section - Only when solution is displayed */}
                {displayMode === 'solution' && (
                    <div className="flex-shrink-0 mb-4">
                        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-6 shadow-lg">
                            <BeTheTeacher />
                        </div>
                    </div>
                )}
                
                {/* Knowledge Reinforcement Section - Map the solution removed */}
                {(showSummary || displayMode === 'solution') && (
                    <div className="flex-grow">
                        <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                            <h3 className="text-lg font-semibold text-purple-400 mb-4 flex items-center">
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7c0-2.21-3.582-4-8-4s-8 1.79-8 4z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3c-4.418 0-8 1.79-8 4s3.582 4 8 4 8-1.79 8-4-3.582-4-8-4z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c-2.209 0-4-1.791-4-4s1.791-4 4-4 4 1.791 4 4-1.791 4-4 4z" />
                                </svg>
                                Knowledge Reinforcement
                            </h3>
                            <p className="text-gray-400 text-sm">
                                Additional learning resources and practice materials will be available here.
                            </p>
                        </div>
                    </div>
                )}                

            </div>
        );
    };

    if (!renderPanels) {
        return (
            <div className="flex items-center justify-center h-screen bg-gray-900">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-screen bg-gray-900 text-gray-200 font-sans p-4 space-y-4">
            {/* Header */}
            <div className="flex justify-between items-center w-full pb-2 border-b border-gray-700">
                <div className="flex items-center space-x-8">
                    <h1 className="text-blue-400 text-3xl font-bold">
                        Quizz it !
                    </h1>
                    <QuizModeSelector />
                </div>
                <ContextDisplay context={sessionContext} />
            </div>

            {/* Main Content */}
            <PanelGroup direction="horizontal" className="flex-grow w-full rounded-xl shadow-2xl overflow-hidden border border-gray-700">
                {/* Left Panel - QCM Content */}
                <Panel defaultSize={60} minSize={30} className="flex flex-col p-6 bg-gray-800 border-r border-gray-700 overflow-y-auto custom-scrollbar rounded-l-xl relative">
                    
                    {/* QCM Content */}
                    <div className="flex-grow overflow-y-auto pr-4 mb-6 custom-scrollbar">
                        <div className="mb-6 mt-12 pb-4 text-left">
                            {/* Open Question - positioned above horizontal line, aligned right */}
                            <OpenQuestion />
                            
                            {/* Horizontal line separator */}
                            <div className="border-b border-gray-700 mb-4"></div>
                            
                            {/* Feedback Display */}
                            {feedback && <FeedbackDisplay feedback={feedback} />}
                        </div>

                        {/* Conditional rendering based on QCM type */}
                        {currentQCM?.questionType === 'funnel-qcm' ? (
                            /* Funnel QCM View */
                            <FunnelQCMViewer />
                        ) : (
                            /* Standard QCM View */
                            <>

                                {/* Two Column Layout: Sections + Widgets */}
                                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
                                    {/* Left Column: Sections */}
                                    <div className="lg:col-span-3 space-y-6">
                                        {/* Theme Section */}
                                        <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-lg">
                                            <div className="flex items-center mb-4">
                                                <div className="mr-3">
                                                    <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                    </svg>
                                                </div>
                                                <h2 className="text-xl font-bold text-blue-300">{t('theme') || 'Thème'}</h2>
                                            </div>
                                            <div className="text-lg leading-relaxed">
                                                <SectionContent content={currentQCM?.theme} />
                                            </div>
                                        </div>

                                        {/* Context Section */}
                                        <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-green-500 shadow-lg">
                                            <div className="flex items-center mb-4">
                                                <div className="mr-3">
                                                    <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                    </svg>
                                                </div>
                                                <h2 className="text-xl font-bold text-green-300">{t('context') || 'Context'}</h2>
                                            </div>
                                            <div className="text-lg leading-relaxed">
                                                <SectionContent content={currentQCM?.context} />
                                            </div>
                                        </div>

                                        {/* Score Display for Exam Style - Above Question Section */}
                                        {currentQuestionType === 'qcm' && displayMode === 'solution' && (
                                            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-lg mb-6">
                                                <div className="flex items-center mb-4">
                                                    <div className="mr-3">
                                                        <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <h3 className="text-lg font-semibold text-blue-300">Score Evaluation</h3>
                                                </div>
                                                
                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                    {/* Raw Score */}
                                                    <div className="bg-gray-700 rounded-lg p-4">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-blue-400">
                                                                {currentQCM?.examScore?.raw || 0}
                                                            </div>
                                                            <div className="text-sm text-gray-400">Raw Score</div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Calculated from selections
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Weighted Score */}
                                                    <div className="bg-gray-700 rounded-lg p-4">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-green-400">
                                                                {currentQCM?.examScore?.weighted || 0}
                                                            </div>
                                                            <div className="text-sm text-gray-400">Weighted Score</div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                With coefficients applied
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Performance */}
                                                    <div className="bg-gray-700 rounded-lg p-4">
                                                        <div className="text-center">
                                                            <div className="text-2xl font-bold text-orange-400">
                                                                {currentQCM?.examScore?.percentage || 0}%
                                                            </div>
                                                            <div className="text-sm text-gray-400">Performance</div>
                                                            <div className="text-xs text-gray-500 mt-1">
                                                                Based on accuracy
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Question Section */}
                                        <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-orange-500 shadow-lg min-h-[200px]">
                                            <div className="flex items-center mb-4">
                                                <div className="mr-3">
                                                    <svg className="w-5 h-5 text-orange-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                        <path d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                                    </svg>
                                                </div>
                                                <h2 className="text-xl font-bold text-orange-300">Question</h2>
                                            </div>
                                            <div className="text-lg leading-relaxed">
                                                {displayMode === 'question' && <SectionContent content={currentQCM?.qcm?.question} />}
                                                {displayMode === 'qcm' && (
                                                    currentQuestionType === 'qcm' ? 
                                                        <ExamQCMOptions /> : 
                                                        <QCMOptions />
                                                )}
                                                {displayMode === 'solution' && <QCMSolution />}
                                                {displayMode === 'help' && <QCMHelp />}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column: Widgets */}
                                    <div className="lg:col-span-1">
                                        {(() => {
                                            const isExamStyle = currentQuestionType === 'qcm';
                                            // Debug pour le score
                                            console.log('🎯 Mode detection debug:', {
                                                currentQuestionType,
                                                isExamStyle,
                                                qcmQuestionType: currentQCM?.qcm?.questionType,
                                                qcmMode: currentQCM?.qcm?.mode,
                                                correctAnswers: currentQCM?.qcm?.correct_answer
                                            });
                                            return (
                                                <InfoPanel 
                                                    elapsedTime={elapsedTime}
                                                    maxTime={maxTime}
                                                    attemptCount={attemptCount}
                                                    difficulty={currentQCM?.metadata?.difficulty}
                                                    degreeOfCertainty={degreeOfCertainty}
                                                    hoveredCertainty={hoveredCertainty}
                                                    showCertainty={displayMode === 'qcm'}
                                                    isTimerRunning={isTimerRunning}
                                                    qcmId={currentQCM?.qcm_id}
                                                    // Props pour le mode exam style
                                                    isExamStyle={isExamStyle}
                                                    selectedAnswers={new Set()}
                                                    correctAnswers={currentQCM?.qcm?.correct_answer || []}
                                                    selectedCertainties={new Map()}
                                                />
                                            );
                                        })()}
                                    </div>
                                </div>

                                {/* LLM Evaluation Section - Show for all free text answers */}
                                {currentQCM?.llmEvaluation && currentQCM.llmEvaluation.score !== undefined && (
                                    <div className="mb-6">
                                            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-purple-500 shadow-lg">
                                                <div className="flex items-center mb-4">
                                                    <div className="mr-3">
                                                        <svg className="w-5 h-5 text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <h2 className="text-xl font-bold text-purple-300">Evaluation of the given answer</h2>
                                                </div>
                                                <div className="text-lg leading-relaxed">
                                                    <div className="mb-4">
                                                        <p className="text-gray-300 mb-2">
                                                            <span className="font-semibold text-green-400">Score:</span> {currentQCM.llmEvaluation.score}/100 
                                                            <span className="text-gray-500 ml-2">(Threshold: {currentQCM.llmEvaluation.threshold})</span>
                                                        </p>
                                                        <p className="text-gray-300">
                                                            <span className="font-semibold text-blue-400">Explanation:</span> {currentQCM.llmEvaluation.explanation}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Evaluation Loading Indicator */}
                                {evaluationLoading && (
                                    <div className="mb-6">
                                            <div className="bg-gray-800 rounded-lg p-6 border-l-4 border-blue-500 shadow-lg">
                                                <div className="flex items-center mb-4">
                                                    <div className="mr-3">
                                                        <svg className="w-5 h-5 text-blue-400 animate-spin" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                        </svg>
                                                    </div>
                                                    <h2 className="text-xl font-bold text-blue-300">Evaluating your answer...</h2>
                                                </div>
                                                <div className="text-lg leading-relaxed">
                                                    <p className="text-gray-300">
                                                        Please wait while the AI evaluates your response...
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                {/* Navigation Buttons */}
                                {options.length > 0 && (
                                    <div className="mt-6 pt-6 border-t border-dashed border-blue-400 flex flex-col items-center gap-4">
                                        <div className="flex flex-wrap justify-center gap-3">
                                            {options.map((option) => (
                                                <button
                                                    key={option.id}
                                                    type="button"
                                                    onClick={option.action}
                                                    className="font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95 bg-blue-600 hover:bg-blue-700 text-white"
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* QCM Rating Section - Version Téléphone uniquement */}
                                {displayMode === 'solution' && (
                                    <div className="lg:hidden mt-6 pt-6 border-t border-dashed border-gray-600">
                                        <div className="bg-gray-800 rounded-lg p-4">
                                            <h3 className="text-lg font-semibold text-blue-400 mb-4 text-center">
                                                {t('rate_this_qcm') || 'Rate this QCM'}
                                            </h3>
                                            <QCMRating 
                                                qcmId={currentQCM?.qcm_id || currentQCM?._id} 
                                                onRatingChange={(rating) => {
                                                    console.log('Note mise à jour:', rating);
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                </Panel>

                <PanelResizeHandle className="w-2 bg-gray-700 hover:bg-blue-500 cursor-ew-resize transition-colors duration-200" />

                {/* Right Panel */}
                <Panel defaultSize={40} minSize={20} className="flex flex-col h-full bg-gray-800 rounded-r-xl">
                    <Tabs className="react-tabs-custom flex flex-col h-full">
                        <TabList className="flex border-b border-gray-700 bg-gray-900 rounded-tr-xl">
                            <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                {showSummary || displayMode === 'solution' ? t('summary') : t('response')}
                            </Tab>
                            <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                📊 Dashboard
                            </Tab>
                            <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                {t('notes') || 'Notes'}
                            </Tab>
                            <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                📈 Stats
                            </Tab>
                        </TabList>

                        {/* Response Tab */}
                        <TabPanel className="flex-grow bg-gray-800 overflow-y-auto custom-scrollbar">
                            {renderRightPanel()}
                        </TabPanel>

                        {/* Dashboard Tab */}
                        <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                            <QCMDashboard />
                        </TabPanel>

                        {/* Notes Tab */}
                        <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                            <NotesEditor />
                        </TabPanel>

                        {/* Stats Tab */}
                        <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                            <UserMetricsPanel sessionContext={sessionContext} />
                        </TabPanel>
                    </Tabs>
                </Panel>
            </PanelGroup>
        </div>
    );


};

export default QCMViewer;